/**
 * FormaLeftRail — the 176px navigation column mirroring Forma Data
 * Management's left rail. Only "Vera Compliance" is the active item
 * (highlighted in Forma blue); everything else is decorative,
 * matching the SVG mockup.
 */
import { FORMA } from "./FormaTokens";

interface RailItem {
  label: string;
  icon: string;
  badge?: string;
  highlight?: boolean;
}

const RAIL_ITEMS: RailItem[] = [
  { label: "Files", icon: "📁" },
  { label: "Specifications", icon: "▢" },
  { label: "Reviews", icon: "✓" },
  { label: "Transmittals", icon: "≡" },
  { label: "Issues", icon: "◯", badge: "12" },
  { label: "Reports", icon: "▤" },
  { label: "Members", icon: "👥" },
  { label: "Boards", icon: "▣" },
  { label: "Bridge", icon: "⇄" },
  { label: "Vera Compliance", icon: "✦", highlight: true },
  { label: "Settings", icon: "⚙" },
];

export function FormaLeftRail({
  activeLabel = "Files",
  onFilesClick,
}: {
  /** Which rail item shows the active (blue) state. */
  activeLabel?: string;
  /** When set, the "Files" item becomes a button that returns to the Files screen. */
  onFilesClick?: () => void;
} = {}) {
  return (
    <nav
      aria-label="Forma Data Management sections"
      style={{
        width: 176,
        background: "#f8f9fb",
        borderRight: `1px solid ${FORMA.borderSoft}`,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        padding: "8px 0",
        overflowY: "auto",
      }}
    >
      {RAIL_ITEMS.map(item => (
        <RailRow
          key={item.label}
          item={item}
          isActive={item.label === activeLabel}
          onClick={item.label === "Files" ? onFilesClick : undefined}
        />
      ))}
    </nav>
  );
}

function RailRow({
  item,
  isActive,
  onClick,
}: {
  item: RailItem;
  isActive: boolean;
  onClick?: () => void;
}) {
  const isHighlight = !!item.highlight;
  const style = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    margin: "2px 8px",
    borderRadius: 6,
    border: "none",
    width: "calc(100% - 16px)",
    textAlign: "left",
    background: isHighlight ? FORMA.blueSoftBg : "transparent",
    color: isHighlight || isActive ? FORMA.blue : FORMA.textSecondary,
    fontWeight: isHighlight || isActive ? 600 : 400,
    fontSize: 13,
    fontFamily: "inherit",
    cursor: onClick ? "pointer" : "default",
  } as const;
  const children = (
    <>
      {isActive && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: -10,
            top: 4,
            bottom: 4,
            width: 4,
            background: FORMA.blue,
            borderRadius: 2,
          }}
        />
      )}
      <span aria-hidden style={{ width: 16, textAlign: "center" }}>
        {item.icon}
      </span>
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.badge && (
        <span
          style={{
            background: FORMA.blue,
            color: "#fff",
            borderRadius: 999,
            padding: "1px 8px",
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          {item.badge}
        </span>
      )}
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} style={style}>
      {children}
    </button>
  ) : (
    <div style={style}>{children}</div>
  );
}
